import { Injectable } from '@angular/core';
import { BaseService } from '@core/services/base.service';
import { MasterList } from '@core/models/master-list.model';
import { map, Observable } from 'rxjs';
import {
  Vehicle,
  VehicleAvailableMaturityTypesParams,
  VehicleDriver,
  VehicleDriverFilters,
  VehicleDriverForm,
  VehicleExpiration,
  VehicleExpirationDuplicateParams,
  VehicleExpirationDuplicateResponse,
  VehicleExpirationFilters,
  VehicleExpirationForm,
  VehicleExpirationHistory,
  VehicleExpirationRenewPayload,
  VehicleFile,
  VehicleChecklist,
  VehicleChecklistFilters,
  VehicleFileFilters,
  VehicleFileForm,
  VehicleFilters,
  VehicleForm,
  VehicleByState,
  VehicleByStateFilter,
  VehicleDisposalEligibility,
  VehicleLicensePlateValidation,
  VehicleLicensePlateValidationParams,
  VehicleEventuality,
  VehicleEventualityFilters,
  VehicleEventualityForm
} from '@features/vehicles/interfaces/vehicle.interface';
import { ApiResponsePaged } from '@core/interfaces/api.interface';
import { mapPagedResponse } from '@core/utils/mapper-response-paged.util';
import { HttpResponse } from '@angular/common/http';
import { ReportAlertChecklist, ReportAlertChecklistFilters } from '@features/reports/interfaces/report-alert-checklist.interface';

@Injectable({
  providedIn: 'root',
})
export class VehiclesService extends BaseService {
  constructor() {
    super('vehicles');
  }

  getAll(params: VehicleFilters): Observable<ApiResponsePaged<Vehicle>> {
    return this.GET<HttpResponse<Vehicle[]>>('paged', params, {
      observe: 'response',
    }).pipe(mapPagedResponse<Vehicle>());
  }

  getById(id: string): Observable<Vehicle> {
    return this.GET<Vehicle>(id);
  }

  /**
   * @description
   * Verifica si una placa ya está registrada en el negocio. En edición, excluye el propio
   * vehículo (`excludeVehicleId`) para permitir conservar su propia placa.
   * @param {VehicleLicensePlateValidationParams} params Placa, negocio y, en edición, el id a excluir.
   * @returns {Observable<boolean>} `true` si la placa ya existe en otro vehículo.
   */
  validateLicensePlate(params: VehicleLicensePlateValidationParams): Observable<boolean> {
    return this.GET<VehicleLicensePlateValidation>('validate-license-plate', params).pipe(
      map((response) => response.exists === true),
    );
  }

  /**
   * @description
   * Obtiene contadores de vehículos agrupados por estado (`vehiclesByState`).
   * @param {VehicleByStateFilter} [filter] Filtros opcionales de negocio y flota.
   * @returns {Observable<VehicleByState[]>} Totales por estado devueltos por el backend.
   */
  getByState(filter?: VehicleByStateFilter): Observable<VehicleByState[]> {
    return this.GET<VehicleByState[]>('vehiclesByState', filter);
  }

  create(payload: Omit<VehicleForm, 'id' | 'checklist'>): Observable<Vehicle> {
    return this.POST<Vehicle>('', payload);
  }

  update(id: string, payload: Partial<VehicleForm>): Observable<Vehicle> {
    return this.PUT<Vehicle>(id, payload);
  }

  deleteVehicle(id: string): Observable<void> {
    return this.DELETE<void>(id);
  }

  /**
   * @description
   * Verifica si el vehículo puede darse de baja o si tiene relaciones en otros
   * módulos que lo impiden.
   * @param {string} id Identificador del vehículo.
   * @returns {Observable<VehicleDisposalEligibility>}
   */
  getDisposalEligibility(id: string): Observable<VehicleDisposalEligibility> {
    return this.GET<VehicleDisposalEligibility>(`${id}/disposal-eligibility`);
  }

  exportVehicles(params: VehicleFilters): Observable<HttpResponse<Blob>> {
    return this.GET<HttpResponse<Blob>>('excel', params, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  /**
   * @description
   * Carga masiva de vehículos desde un archivo Excel.
   * El backend valida el archivo y responde con éxito o un Excel de errores.
   * @param {string} businessId ID de la empresa.
   * @param {File} file Archivo Excel diligenciado.
   * @returns {Observable<HttpResponse<Blob>>} Respuesta del backend.
   */
  bulkUpload(businessId: string, file: File): Observable<HttpResponse<Blob>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.POST<HttpResponse<Blob>>(`upload/${businessId}`, formData, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  /**
   * * Obtiene los vencimientos de vehículos.
   * @param filters - Filtros de los vencimientos de vehículos.
   * @returns Observable<ApiResponsePaged<VehicleExpiration>> Observable que emite los vencimientos de vehículos.
   */
  getVehiclesExpirations(
    filters: VehicleExpirationFilters,
  ): Observable<ApiResponsePaged<VehicleExpiration>> {
    return this.GET<HttpResponse<VehicleExpiration[]>>('maturities', filters, {
      observe: 'response',
    }).pipe(mapPagedResponse<VehicleExpiration>());
  }

  /** Mismos filtros que `getVehiclesExpirations`; el backend genera el Excel sin paginación. */
  exportVehiclesExpirationsExcel(params: VehicleExpirationFilters): Observable<Blob> {
    return this.GET<Blob>('maturities/ReportExcel', params, { responseType: 'blob' });
  }

  createVehicleExpiration(data: Partial<VehicleExpirationForm>): Observable<VehicleExpiration> {
    return this.POST<VehicleExpiration>('maturities', data);
  }

  /**
   * * Actualiza un vencimiento de vehículo.
   * El backend responde sin cuerpo, por lo que puede emitir `null`.
   * @param id - ID del vencimiento de vehículo.
   * @param data - Datos del vencimiento de vehículo.
   * @returns Observable<VehicleExpiration | null> Vencimiento actualizado o `null` si la respuesta viene vacía.
   */
  updateVehicleExpiration(
    id: string,
    data: Partial<VehicleExpirationForm>,
  ): Observable<VehicleExpiration | null> {
    return this.PUT<VehicleExpiration | null>(`maturities/${id}`, data);
  }

  /**
   * * Renueva un vencimiento de vehículo.
   * El backend responde sin cuerpo, por lo que puede emitir `null`.
   * @param id - ID del vencimiento de vehículo.
   * @param data - Fechas, archivo y usuario de la renovación.
   * @returns Observable<VehicleExpiration | null> Vencimiento renovado o `null` si la respuesta viene vacía.
   */
  renewVehicleExpiration(
    id: string,
    data: VehicleExpirationRenewPayload,
  ): Observable<VehicleExpiration | null> {
    return this.POST<VehicleExpiration | null>(`maturities/${id}/renew`, data);
  }

  /**
   * * Valida si ya existe un vencimiento del mismo tipo para el vehículo.
   * El backend responde `{ hasDuplicate: boolean }`; se normaliza a booleano.
   * @param params - Vehículo, tipo de vencimiento y, en edición, el id a excluir.
   * @returns Observable<boolean> `true` si el vencimiento está duplicado.
   */
  validateDuplicateVehicleExpiration(
    params: VehicleExpirationDuplicateParams,
  ): Observable<boolean> {
    return this.GET<VehicleExpirationDuplicateResponse | boolean>(
      'maturities/validate-duplicate',
      params,
    ).pipe(
      map((response) =>
        typeof response === 'boolean' ? response : response?.hasDuplicate === true,
      ),
    );
  }

  /**
   * @description
   * Obtiene los tipos de vencimiento disponibles para un vehículo: el backend ya excluye
   * los tipos que el vehículo tiene activos/pendientes, evitando validar duplicados en el
   * cliente contra el catálogo completo.
   * @param {VehicleAvailableMaturityTypesParams} params Negocio y vehículo.
   * @returns {Observable<MasterList[]>}
   */
  getAvailableMaturityTypes(
    params: VehicleAvailableMaturityTypesParams,
  ): Observable<MasterList[]> {
    return this.GET<MasterList[]>('maturities/available-types', params);
  }

  deleteVehicleExpiration(id: string): Observable<void> {
    return this.DELETE<void>(`maturities/${id}`);
  }

  sendEmailVehicleExpiration(id: string): Observable<void> {
    return this.POST<void>(`maturities/${id}/notify`, {});
  }

  /**
   * * Agrega un historial de vencimiento de vehículo.
   * @param data - Datos del historial de vencimiento de vehículo.
   * @returns Observable<void> Observable que emite el void.
   */
  addVehicleExpirationHistory(data: VehicleExpirationHistory): Observable<void> {
    return this.POST<void>('maturities/history', data);
  }

  /**
   * * Exporta los vencimientos de vehículos.
   * @param filters - Filtros de los vencimientos de vehículos.
   * @returns Observable<HttpResponse<Blob>> Observable que emite el Blob del archivo exportado.
   */
  export(filters: VehicleExpirationFilters): Observable<HttpResponse<Blob>> {
    return this.GET<HttpResponse<Blob>>('maturities/excel', filters, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  /**
   * * Obtiene los conductores asignados a un vehículo.
   * @param filters - Filtros de los conductores asignados a un vehículo.
   * @returns Observable<ApiResponsePaged<VehicleDriver>> Observable que emite los conductores asignados a un vehículo.
   */
  getDriversByVehicle(filters: VehicleDriverFilters): Observable<ApiResponsePaged<VehicleDriver>> {
    return this.GET<HttpResponse<VehicleDriver[]>>(`drivers`, filters, {
      observe: 'response',
    }).pipe(mapPagedResponse<VehicleDriver>());
  }

  /**
   * * Agrega un conductor a un vehículo.
   * @param payload - Datos del conductor a agregar.
   * @returns Observable<VehicleDriver> Observable que emite el conductor agregado.
   */
  addDriver(payload: VehicleDriverForm): Observable<VehicleDriver> {
    return this.POST<VehicleDriver>('drivers', payload);
  }

  /**
   * * Elimina un conductor de un vehículo.
   * @param vehicleDriverId - ID del conductor a eliminar.
   * @returns Observable<void> Observable que emite el void.
   */
  removeDriver(vehicleDriverId: string): Observable<void> {
    return this.DELETE<void>(`drivers/${vehicleDriverId}`);
  }

  /**
   * * Obtiene los archivos de un vehículo.
   * @param filters - Filtros de los archivos del vehículo.
   * @returns Observable<VehicleFile[]> Observable que emite los archivos del vehículo.
   */
  getVehicleFile(filters: VehicleFileFilters): Observable<VehicleFile[]> {
    return this.GET<VehicleFile[]>('files', filters);
  }

  /**
   * * Agrega un archivo a un vehículo.
   * @param payload - Datos del archivo a agregar.
   * @returns Observable<VehicleFile> Observable que emite el archivo agregado.
   */
  addVehicleFile(payload: VehicleFileForm): Observable<VehicleFile> {
    return this.POST<VehicleFile>('files', payload);
  }

  /**
   * * Elimina un archivo de un vehículo.
   * @param id - ID del archivo a eliminar.
   * @returns Observable<void> Observable que emite el void.
   */
  deleteVehicleFile(id: string): Observable<void> {
    return this.DELETE<void>(`files/${id}`);
  }

  /**
   * * Sube un archivo a un vehículo.
   * @param payload - Datos del archivo a subir.
   * @returns Observable<VehicleFile> Observable que emite el archivo subido.
   */
  addListVehicleFile(payload: Omit<VehicleFileForm, 'files'>[]): Observable<any> {
    return this.POST<any>('files/list', payload);
  }

  /**
   * Consulta asociaciones vehículo ↔ lista de chequeo.
   * @param filters - `vehicleId` para listas del vehículo; `checkListId` para vehículos de la lista.
   */
  getChecklistsByVehicle(
    filters: VehicleChecklistFilters,
  ): Observable<ApiResponsePaged<VehicleChecklist>> {
    return this.GET<HttpResponse<VehicleChecklist[]>>(`checklists`, filters, {
      observe: 'response',
    }).pipe(mapPagedResponse<VehicleChecklist>());
  }

  /**
   * * Elimina una checklist de un vehículo.
   * @param id - ID de la checklist a eliminar.
   * @returns Observable<void> Observable que emite el void.
   */
  deleteListVehicleChecklist(id: string): Observable<void> {
    return this.DELETE<void>(`checklists/${id}`);
  }

  /**
   * * Agrega una lista de checklists a un vehículo.
   * @param payload - Datos de las checklists a agregar.
   * @returns Observable<void> Observable que emite el void.
   */
  addListVehicleChecklist(checklist: VehicleChecklist[]): Observable<void> {
    return this.POST<void>(`checklists/list`, checklist);
  }

  /**
   * * Obtiene las eventualidades de un vehículo.
   * @param params - Filtros de las eventualidades de un vehículo.
   * @returns Observable<ApiResponsePaged<VehicleEventualityView>> Observable que emite las eventualidades de un vehículo.
   */
  getVehicleEventualities(params: VehicleEventualityFilters): Observable<ApiResponsePaged<VehicleEventuality>> {
    return this.GET<HttpResponse<VehicleEventuality[]>>('eventualities', params, {
      observe: 'response',
    }).pipe(mapPagedResponse<VehicleEventuality>());
  }
  
  createVehicleEventuality(payload: Omit<VehicleEventualityForm, 'id'>): Observable<VehicleEventuality> {
    return this.POST<VehicleEventuality>('eventualities', payload);
  }

  updateVehicleEventuality(id: string, payload: VehicleEventualityForm): Observable<VehicleEventuality> {
    return this.PUT<VehicleEventuality>(`eventualities/${id}`, payload);
  }

  /** Mismos filtros que `getVehicleEventualities`; el backend genera el Excel sin paginación. */
  exportVehicleEventualitiesExcel(params: VehicleEventualityFilters): Observable<Blob> {
    return this.GET<Blob>('eventualities/ReportsExport', params, { responseType: 'blob' });
  }

  getVehicleChecklistAlerts(params: ReportAlertChecklistFilters): Observable<ApiResponsePaged<ReportAlertChecklist>> {
    return this.GET<HttpResponse<ReportAlertChecklist[]>>('alerts/lastDate', params, {
      observe: 'response',
    }).pipe(mapPagedResponse<ReportAlertChecklist>());
  }


}
